<template>
    <div>
        <page-header
            :text="$t('navigation.organization_overview')"
            :searchField="{
                placeholder: $t('organizations.search'),
                inputEmitEvent: 'search',
                allowedFields: searchFields,
                savedSearchName: 'search-organization-overview',
            }"
            :button="
                $can('csv export', 'Systems')
                    ? {
                          text: $t('organizations.export_csv'),
                          emitEvent: 'exportCSV',
                          ability: true,
                          loading: csvLoading,
                      }
                    : false
            "
            @exportCSV="exportCSVHandler"
            @search="searchHandler"
        />
        <div class="table-wrapper">
            <table class="table table-filter table--striped table--filterable">
                <thead>
                    <tr>
                        <th v-for="field in fields" :key="field.value" class="table-filter__trigger" :width="field.width">
                            <div>{{ field.title }}</div>
                            <div
                                v-if="field.sortable"
                                class="table-filter__sorting"
                                @click="sortClickHandler(field.value)"
                                :class="{
                                    asc: activeSorting[field.value] === 'asc',
                                    desc: activeSorting[field.value] === 'desc',
                                }"
                            >
                                <i class="icon icon--common-caret"></i>
                            </div>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="organization in filteredOrganizations" :key="organization.id">
                        <td>
                            <OrganizationBreadcrumbs :breadcrumbs="organization.breadcrumbs" />
                        </td>
                        <td>
                            <router-link
                                :to="{
                                    name: 'organization-management',
                                    params: { id: organization.id },
                                }"
                            >
                                <b>{{ organization.name }}</b>
                            </router-link>
                        </td>
                        <td>{{ organization.email }}</td>
                        <td>{{ organization.postcode }}</td>
                        <td>{{ organization.city }}</td>
                        <td class="uppercase">{{ organization.country }}</td>
                        <td>
                            <router-link
                                :to="{
                                    name: 'organization-system-service',
                                    params: { id: organization.id },
                                }"
                                class="small-link"
                            >
                                {{ organization.serviced_systems_count }}
                            </router-link>
                        </td>
                        <td>
                            <router-link
                                :to="{
                                    name: 'organization-system-operate',
                                    params: { id: organization.id },
                                }"
                                class="small-link"
                                >{{ organization.operated_systems_count }}
                            </router-link>
                        </td>
                    </tr>
                </tbody>

                <TablePagination
                    v-if="paginationData"
                    :paginationData="paginationData"
                    :visibleAmount="searchedItems ? searchedItems.length : items.length"
                    @goToPrevPage="prevPageHandler"
                    @goToNextPage="nextPageHandler"
                />
            </table>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import TablePagination from '@/components/TablePagination';
import OrganizationBreadcrumbs from '@/components/OrganizationBreadcrumbs';
import paginatedTable from '@/mixins/paginatedTable';
import { createSearchParams } from '@/utils/searchParameters';
import { startCSVDownload, prepareCSVData } from '@/utils/csvExport';

export default {
    components: {
        PageHeader,
        TablePagination,
        OrganizationBreadcrumbs,
    },
    mixins: [paginatedTable],
    data() {
        return {
            csvLoading: false,
            apiType: 'organizations',
            searchApi: 'search',
            responseField: 'organizations',
            searchFields: ['name', 'email', 'postcode', 'city', 'country'],
            fields: [
                {
                    value: 'breadcrumbs',
                    title: this.$t('systems.organizations'),
                    sortable: false,
                    width: '20%',
                },
                {
                    value: 'name',
                    title: this.$t('partners.name'),
                    sortable: true,
                    width: '15%',
                },
                {
                    value: 'email',
                    title: this.$t('partners.email'),
                    sortable: true,
                    width: '15%',
                },
                {
                    value: 'postcode',
                    title: this.$t('partners.postcode'),
                    sortable: true,
                    width: '10%',
                },
                {
                    value: 'city',
                    title: this.$t('partners.city'),
                    sortable: true,
                    width: '15%',
                },
                {
                    value: 'country',
                    title: this.$t('partners.country'),
                    sortable: true,
                    width: '5%',
                },
                {
                    value: 'serviced_systems_count',
                    title: this.$t('organizations.amount_services'),
                    sortable: false,
                    width: '5%',
                },
                {
                    value: 'operated_systems_count',
                    title: this.$t('organizations.amount_operators'),
                    sortable: false,
                    width: '5%',
                },
            ],
        };
    },
    created() {
        this.loadItems();
    },
    computed: {
        filteredOrganizations() {
            if (!this.items) return [];

            let filtered = this.searchedItems ? [...this.searchedItems] : [...this.items];
            return filtered;
        },
    },
    methods: {
        async exportCSVHandler() {
            if (this.csvLoading) return;
            const fields = [
                {
                    name: this.$t('fields.organization_name'),
                    value: 'name',
                },
                {
                    name: this.$t('fields.address1'),
                    value: 'address1',
                },
                {
                    name: this.$t('fields.address2'),
                    value: 'address2',
                },
                {
                    name: this.$t('fields.postcode'),
                    value: 'postcode',
                },
                {
                    name: this.$t('fields.city'),
                    value: 'city',
                },
                {
                    name: this.$t('fields.country'),
                    value: 'country',
                    action: 'uppercase',
                },
                {
                    name: this.$t('fields.email'),
                    value: 'email',
                },
                {
                    name: this.$t('professional'),
                    value: 'professional',
                    action: 'translate',
                    translations: {
                        0: this.$t('partners.qualification0'),
                        1: this.$t('partners.qualification1'),
                        2: this.$t('partners.qualification2'),
                    },
                },
            ];
            this.csvLoading = true;
            try {
                let response;
                const itemsPerPage = 100;
                const allowedParams = this.searchFields;
                if (this.activeSearchTerm) {
                    const searchParams = createSearchParams(this.activeSearchTerm, allowedParams);
                    const params = {
                        ...searchParams,
                        per_page: itemsPerPage,
                    };
                    response = await api.organizations.search(params);
                } else {
                    response = await api.organizations.search({ per_page: itemsPerPage });
                }

                let orgs = response ? response.data.organizations : false;

                if (response.data._metadata && response.data._metadata.total_count > itemsPerPage) {
                    const pages = response.data._metadata.total_pages;
                    const startPage = response.data._metadata.current_page + 1; // we already have the results for the first page

                    for (let i = startPage; i <= pages; i++) {
                        let request;
                        if (this.activeSearchTerm) {
                            const searchParams = createSearchParams(this.activeSearchTerm, allowedParams);

                            const params = {
                                ...searchParams,
                                per_page: itemsPerPage,
                                page: i,
                            };
                            request = await api.organizations.search(params);
                        } else {
                            request = await api.organizations.search({
                                per_page: itemsPerPage,
                                page: i,
                            });
                        }

                        if (request.data.organizations) {
                            if (!orgs) orgs = [];
                            orgs = [...orgs, ...request.data.organizations];
                        }
                    }
                }

                if (orgs) {
                    const csvData = prepareCSVData(fields, orgs);
                    startCSVDownload(csvData, 'export-organizations');
                    this.csvLoading = false;
                }
            } catch (error) {
                console.error(error);
                this.csvLoading = false;
            }
        },
    },
};
</script>
